import { z } from "zod";
import { CryptoMarket } from "../api/crypto-api";
import { Solana } from "../solana/solana-base";
import { SolanaProgram } from "../util/solana-program";
import { CoerceBooleanZ } from "./zod-helpers";

export namespace NftTypes {
  export const NftTraitValueZ = z.string().or(z.number()).or(z.boolean());
  export type NftTraitValue = z.infer<typeof NftTraitValueZ>;

  export const NftTraitZ = z.object({
    trait_type: z.string(),
    value: NftTraitValueZ.nullable(),
  });
  export type NftTrait = z.infer<typeof NftTraitZ>;
  export const NftTraitsZ = z.array(NftTraitZ);
  export type NftTraits = z.infer<typeof NftTraitsZ>;
}

const StringOrNumberZ = z.string().or(z.number());

export namespace TokenProgram {
  // This is data stored in the Token Mint account.
  export const MintAccountZ = z.object({
    mint_address: Solana.AddressZ,
    network: z.nativeEnum(Solana.Network),

    supply: z.string(),
    freeze_authority: Solana.AddressZ.nullable(),
    decimals: z.number(),

    type: z.enum(["coin", "nft"]),
  });

  export type MintAccount = z.infer<typeof MintAccountZ>;

  export const CreatorZ = z.object({
    address: Solana.AddressZ,
    verified: CoerceBooleanZ.optional(),
    share: z.number().nullable().optional(),
  });
  export type Creator = z.infer<typeof CreatorZ>;

  // This is the Metadata stored off chain, typically in Arweave.
  // We are parsing the data based on the Metaplex Metadata standard:
  // https://docs.metaplex.com/nft-standard#uri-json-schema
  // Be careful about changing this since the table already exists
  export const NftMetadataZ = z.object({
    animation_url: z.string().nullable(),
    collection_name: z
      .string()
      .transform((str) => str.replace(/\0/g, ""))
      .nullable(),
    description: z.string().nullable(),
    name: z.string().nullable(),
    external_url: z.string().nullable(),
    files: z
      .array(z.object({ uri: z.string(), type: z.string().optional() }))
      .nullable(),
    image: z.string().nullable(),
    traits: NftTypes.NftTraitsZ.nullable(),
  });
  export type NftMetadata = z.infer<typeof NftMetadataZ>;

  // This is Metadata that is stored on chain in a PDA account derived from the mint account.
  // This Metadata should be set up for NFTs, but most coins do not have this information.
  export const MetaplexMetadataZ = z.object({
    mint_address: Solana.AddressZ,
    name: z.string(),
    symbol: z.string(),
    uri: z.string(),

    seller_fee_basis_points: StringOrNumberZ.nullable().optional(),
    creators: z.array(CreatorZ),
    metadata_update_authority: Solana.AddressZ.nullable().optional(),

    metaplex_metadata_address: Solana.AddressZ.nullish(),
    metaplex_collection_address: Solana.AddressZ.nullish(),
    metaplex_collection_verified: z.boolean().nullish(),
  });
  export type MetaplexMetadata = z.infer<typeof MetaplexMetadataZ>;

  export const OnChainZ = MintAccountZ.merge(MetaplexMetadataZ);
  export type OnChain = z.infer<typeof OnChainZ>;

  export const NftListingZ = z.object({
    price: Solana.SolAmountZ.nullable(),
    marketplace: Solana.MarketplaceZ.nullable(),
  });

  export type NftListing = z.infer<typeof NftListingZ>;

  export const CoinMetadataZ = z.object({
    description: z.string().nullable().optional(),
    icon_url: z.string().nullable().optional(),
    market_cap_usd: z.string().nullable().optional(),
    price_percent_change_24h: z.string().nullable().optional(),
    price_usd: z.string().nullable().optional(),
    social_links: z.array(z.any()).optional(),
    volume_24h_usd: z.string().nullable().optional(),
    is_swappable: z.boolean(),
  });

  export type CoinMetadata = z.infer<typeof CoinMetadataZ>;

  export const CoinInfoZ = z
    .object({
      // This allows us to do type-narrowing based on token.type
      type: z.literal("coin"),
    })
    .merge(OnChainZ.omit({ type: true, freeze_authority: true }))
    .merge(CoinMetadataZ);

  export type CoinInfo = z.infer<typeof CoinInfoZ>;

  export const CoinBalanceZ = z.object({
    amount: Solana.TokenAmountZ,
    coin: CoinInfoZ,
  });

  export type CoinBalance = z.infer<typeof CoinBalanceZ>;
}

export const toWrappedSol = ({
  network,
  exchange_rate,
  balance,
}: {
  network: Solana.Network;
  exchange_rate: CryptoMarket.ExchangeRate | null;
  balance: Solana.SolAmount;
}): TokenProgram.CoinBalance => {
  return {
    coin: {
      creators: [],
      decimals: Solana.SOL_DECIMALS,
      icon_url: "https://cdn.lu.ma/solana-coin-icons/SOL.png",
      is_swappable: true,
      market_cap_usd: exchange_rate?.market_cap_usd,
      mint_address: SolanaProgram.WRAPPED_SOL,
      name: "Solana",
      network,
      price_percent_change_24h: exchange_rate?.price_percent_change_24h,
      price_usd: exchange_rate?.price_usd,
      social_links: [],
      supply: "600000000000000000",
      symbol: "SOL",
      type: "coin",
      uri: "",
      volume_24h_usd: exchange_rate?.volume_24h_usd,
    },
    amount: { units: balance.lamports },
  };
};
